import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
	selector: 'app-camera-scanner',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './camera-scanner.component.html'
})
export class CameraScannerComponent implements OnInit, OnDestroy {
	@Output() captured = new EventEmitter<Blob>();
	@Output() closed = new EventEmitter<void>();

	stream?: MediaStream;
	isStarting = false;
	error?: string;
	autoEnhance = true;
	brightness = 0; // -100..100
	contrast = 15;  // -100..100
	targetWidth = 1280; // px
	enforcingA4 = true;

	async ngOnInit(): Promise<void> {
		await this.start();
	}

	ngOnDestroy(): void {
		this.stop();
	}

	async start() {
		this.error = undefined;
		this.isStarting = true;
		try {
			this.stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
				audio: false
			});
			const video = document.getElementById('scanVideo') as HTMLVideoElement;
			video.srcObject = this.stream;
			await video.play();
		} catch (e) {
			this.error = 'تعذر الوصول إلى الكاميرا. يرجى السماح بالأذن أو استخدام جهاز مزود بكاميرا.';
		} finally {
			this.isStarting = false;
		}
	}

	stop() {
		this.stream?.getTracks().forEach(t => t.stop());
		this.stream = undefined;
	}

	capture() {
		const video = document.getElementById('scanVideo') as HTMLVideoElement;
		const canvas = document.createElement('canvas');
		const ratio = video.videoWidth / video.videoHeight || 1;
		const width = this.targetWidth;
		canvas.width = width;
		canvas.height = Math.round(width / ratio);
		const ctx = canvas.getContext('2d');
		if (!ctx) return;
		ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
		this.emitProcessed(canvas);
	}

	close() {
		this.stop();
		this.closed.emit();
	}

	private emitProcessed(sourceCanvas: HTMLCanvasElement) {
		let canvas = sourceCanvas;
		// Optional crop to A4 ratio (portrait)
		if (this.enforcingA4) {
			const a4Ratio = 1 / Math.SQRT2; // ~0.707 (w/h) portrait
			const currentRatio = canvas.width / canvas.height;
			let sx = 0, sy = 0, sw = canvas.width, sh = canvas.height;
			if (currentRatio > a4Ratio) {
				// too wide, crop width
				sw = Math.round(canvas.height * a4Ratio);
				sx = Math.floor((canvas.width - sw) / 2);
			} else {
				// too tall, crop height
				sh = Math.round(canvas.width / a4Ratio);
				sy = Math.floor((canvas.height - sh) / 2);
			}
			const cropped = document.createElement('canvas');
			cropped.width = sw;
			cropped.height = sh;
			const cctx = cropped.getContext('2d');
			if (cctx) cctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
			canvas = cropped;
		}
		// Resize again to target width
		if (canvas.width !== this.targetWidth) {
			const resized = document.createElement('canvas');
			const newH = Math.round((this.targetWidth / canvas.width) * canvas.height);
			resized.width = this.targetWidth;
			resized.height = newH;
			const rctx = resized.getContext('2d');
			if (rctx) {
				rctx.imageSmoothingEnabled = true;
				rctx.imageSmoothingQuality = 'high';
				rctx.drawImage(canvas, 0, 0, resized.width, resized.height);
			}
			canvas = resized;
		}
		// Apply brightness/contrast
		if (this.autoEnhance || this.brightness !== 0 || this.contrast !== 0) {
			const ctx = canvas.getContext('2d');
			if (ctx) {
				const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
				const data = img.data;
				// contrast/brightness adjustment
				const b = (this.brightness / 100) * 255;
				const c = (this.contrast / 100) + 1;
				const intercept = 128 * (1 - c);
				for (let i = 0; i < data.length; i += 4) {
					data[i] = Math.max(0, Math.min(255, c * data[i] + intercept + b));
					data[i + 1] = Math.max(0, Math.min(255, c * data[i + 1] + intercept + b));
					data[i + 2] = Math.max(0, Math.min(255, c * data[i + 2] + intercept + b));
				}
				ctx.putImageData(img, 0, 0);
				// Optional light unsharp mask (simple)
				this.applySharpen(canvas, 0.15);
			}
		}
		canvas.toBlob(blob => {
			if (blob) this.captured.emit(blob);
		}, 'image/jpeg', 0.9);
	}

	private applySharpen(canvas: HTMLCanvasElement, amount: number) {
		const ctx = canvas.getContext('2d');
		if (!ctx || amount <= 0) return;
		const w = canvas.width, h = canvas.height;
		const src = ctx.getImageData(0, 0, w, h);
		const dst = ctx.createImageData(w, h);
		const s = src.data, d = dst.data;
		// simple 3x3 kernel: sharpen = original * (1+8a) - a * sum(neighbors)
		const a = amount;
		const stride = w * 4;
		for (let y = 1; y < h - 1; y++) {
			for (let x = 1; x < w - 1; x++) {
				const idx = (y * w + x) * 4;
				for (let c = 0; c < 3; c++) {
					const i = idx + c;
					const center = s[i];
					const n = s[i - stride], spx = s[i + stride], wpx = s[i - 4], epx = s[i + 4];
					const nw = s[i - stride - 4], ne = s[i - stride + 4], sw = s[i + stride - 4], se = s[i + stride + 4];
					const neighbors = n + spx + wpx + epx + nw + ne + sw + se;
					const val = (center * (1 + 8 * a)) - (a * neighbors);
					d[i] = Math.max(0, Math.min(255, val));
				}
				d[idx + 3] = s[idx + 3];
			}
		}
		ctx.putImageData(dst, 0, 0);
	}
}


